import { useCallback, useEffect, useState } from 'react';
import { IServerColumnConfig } from '@/components/table/column-config';
import {
	fetchSurveysTableConfig,
	deleteSurvey,
	fetchSingleSurvey,
	fetchSurveyExport,
	fetchSurveyQuestionTypes,
	fetchSurveysList,
	fetchSurveyStatuses,
	postArchiveSurvey,
	postPublishSurvey,
	postSurvey,
	putSurvey
} from './actions';
import {
	ISurvey,
	ISurveyCreatePayload,
	ISurveyQuestionType,
	ISurveysQuery,
	ISurveyStatus,
	ISurveyUpdatePayload,
	TSurveyExportFormat,
	SurveyQuestionTypesReturnType,
	SurveysReturnType,
	SurveyStatusesReturnType
} from './types';

// ==============================|| CONSULTĂRI (SURVEYS) - HOOK COMUN ||============================== //
// CRUD complet pe contractele BE: listă server-side + detaliu + creare + update +
// publicare/arhivare/ștergere (statusul se schimbă doar prin publish/archive).

// Fallback stabil ca identitate — un `[]` nou pe fiecare randare re-declanșează în lanț
// efectele consumatorilor cu dependență pe `data` (Maximum update depth exceeded).
const EMPTY_SURVEYS: ISurvey[] = [];

export function useSurveys(itemsSet = 10, withTableConfig = false): SurveysReturnType {
	const [data, setData] = useState<ISurvey[] | null>(null);
	// Configurația de coloane (api/surveys/table-config) — cerută doar de pagina de
	// listă (withTableConfig); la eroare rămâne null și tabelul cade silențios pe
	// coloanele definite local.
	const [tableConfig, setTableConfig] = useState<IServerColumnConfig[] | null>(null);
	const [loadingTableConfig, setLoadingTableConfig] = useState<boolean>(
		Boolean(withTableConfig)
	);

	const [loading, setLoading] = useState<boolean>(true);
	const [page, setPage] = useState<number>(0);
	const [items, setItems] = useState<number>(itemsSet);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(0);

	useEffect(() => {
		if (!withTableConfig) return undefined;
		let active = true;
		setLoadingTableConfig(true);
		fetchSurveysTableConfig()
			.then((columns) => {
				if (active) setTableConfig(columns.length ? columns : null);
			})
			.catch(() => {
				// Fallback silențios: fără config BE, tabelul folosește coloanele locale.
				if (active) setTableConfig(null);
			})
			.finally(() => {
				if (active) setLoadingTableConfig(false);
			});
		return () => {
			active = false;
		};
	}, [withTableConfig]);

	const [loadingSurvey, setLoadingSurvey] = useState<boolean>(false);
	const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
	const [loadingAction, setLoadingAction] = useState<boolean>(false);
	const [loadingExport, setLoadingExport] = useState<boolean>(false);

	const getSurveys = useCallback(
		async (query: ISurveysQuery, pageParam: number, itemsParam: number) => {
			try {
				setLoading(true);
				const response = await fetchSurveysList(query, pageParam, itemsParam, (tp, tc) => {
					setTotalPages(tp);
					setTotalItems(tc);
				});
				setData(response);
			} catch {
				setData([]);
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	// Loading separat de cel al listei — detaliul e cerut și din pagini care au deja
	// tabelul montat (ex. duplicare), unde nu vrem să re-intre skeleton-ul listei.
	const getSurvey = useCallback(async (surveyId: number | string) => {
		try {
			setLoadingSurvey(true);
			return await fetchSingleSurvey(surveyId);
		} catch {
			return null;
		} finally {
			setLoadingSurvey(false);
		}
	}, []);

	const createSurvey = useCallback(async (payload: ISurveyCreatePayload) => {
		try {
			setLoadingCreate(true);
			return await postSurvey(payload);
		} catch {
			return null;
		} finally {
			setLoadingCreate(false);
		}
	}, []);

	const updateSurvey = useCallback(
		async (surveyId: number | string, payload: ISurveyUpdatePayload) => {
			try {
				setLoadingAction(true);
				await putSurvey(surveyId, payload);
				return true;
			} catch {
				return false;
			} finally {
				setLoadingAction(false);
			}
		},
		[]
	);

	const publishSurvey = useCallback(async (surveyId: number | string) => {
		try {
			setLoadingAction(true);
			await postPublishSurvey(surveyId);
			return true;
		} catch {
			return false;
		} finally {
			setLoadingAction(false);
		}
	}, []);

	const archiveSurvey = useCallback(async (surveyId: number | string) => {
		try {
			setLoadingAction(true);
			await postArchiveSurvey(surveyId);
			return true;
		} catch {
			return false;
		} finally {
			setLoadingAction(false);
		}
	}, []);

	const removeSurvey = useCallback(async (surveyId: number | string) => {
		try {
			setLoadingAction(true);
			await deleteSurvey(surveyId);
			return true;
		} catch {
			return false;
		} finally {
			setLoadingAction(false);
		}
	}, []);

	const exportSurvey = useCallback(
		async (surveyId: number | string, format: TSurveyExportFormat) => {
			try {
				setLoadingExport(true);
				return await fetchSurveyExport(surveyId, format);
			} catch {
				return null;
			} finally {
				setLoadingExport(false);
			}
		},
		[]
	);

	return {
		tableConfig,
		loadingTableConfig,
		data: data ?? EMPTY_SURVEYS,
		loading,
		getSurveys,
		getSurvey,
		loadingSurvey,
		createSurvey,
		loadingCreate,
		updateSurvey,
		publishSurvey,
		archiveSurvey,
		removeSurvey,
		loadingAction,
		exportSurvey,
		loadingExport,
		page,
		items,
		setPage,
		setItems,
		totalPages,
		totalItems
	};
}

export function useSurveyStatuses(
	getOnRender: boolean | undefined = undefined
): SurveyStatusesReturnType {
	const [data, setData] = useState<ISurveyStatus[]>([]);
	const [loading, setLoading] = useState<boolean>(false);

	const getStatuses = useCallback(async () => {
		try {
			setLoading(true);
			const response = await fetchSurveyStatuses();
			setData(response);
			return response;
		} catch {
			return [];
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (getOnRender) {
			getStatuses();
		}
	}, [getOnRender, getStatuses]);

	return { data, loading, getStatuses };
}

export function useSurveyQuestionTypes(
	getOnRender: boolean | undefined = undefined
): SurveyQuestionTypesReturnType {
	const [data, setData] = useState<ISurveyQuestionType[]>([]);
	const [loading, setLoading] = useState<boolean>(false);

	const getQuestionTypes = useCallback(async () => {
		try {
			setLoading(true);
			const response = await fetchSurveyQuestionTypes();
			setData(response);
			return response;
		} catch {
			return [];
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (getOnRender) {
			getQuestionTypes();
		}
	}, [getOnRender, getQuestionTypes]);

	return { data, loading, getQuestionTypes };
}
